import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Calendar as CalendarIcon,
  Copy,
  Link2,
  Loader2,
  Pause,
  Play,
  RefreshCw,
  Cloud,
  Unplug,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import {
  createAgentCalendar,
  getAgentCalendar,
  patchAgentCalendar,
  pauseAgentCalendar,
  resumeAgentCalendar,
  rotateAgentCalendarPublicToken,
  getCalendarIntegrations,
  startCalendarOAuthGoogle,
  disconnectCalendarIntegration,
  getApiError,
} from '../lib/api';
import { getPublicAppBaseUrl } from '../lib/apiBaseUrl';

const WEEKDAY_META = [
  { weekday: 0, label: 'Sunday' },
  { weekday: 1, label: 'Monday' },
  { weekday: 2, label: 'Tuesday' },
  { weekday: 3, label: 'Wednesday' },
  { weekday: 4, label: 'Thursday' },
  { weekday: 5, label: 'Friday' },
  { weekday: 6, label: 'Saturday' },
];

function defaultWorkingHours() {
  return [1, 2, 3, 4, 5].map((weekday) => ({
    weekday,
    intervals: [{ start: '09:00', end: '17:00' }],
  }));
}

function normalizeWorkingHours(raw) {
  if (!Array.isArray(raw) || raw.length === 0) {
    return defaultWorkingHours();
  }
  const byDay = new Map(raw.map((row) => [Number(row.weekday), row]));
  return WEEKDAY_META.map(({ weekday }) => {
    const found = byDay.get(weekday);
    if (!found || !Array.isArray(found.intervals) || found.intervals.length === 0) {
      return { weekday, intervals: [], closed: true };
    }
    return {
      weekday,
      intervals: found.intervals.map((i) => ({
        start: i.start || '09:00',
        end: i.end || '17:00',
      })),
      closed: false,
    };
  });
}

function pickCalendarPayload(data) {
  if (!data || typeof data !== 'object') return {};
  return data.calendar || data;
}

function getPublicToken(cal) {
  return cal?.public_token || cal?.publicToken || '';
}

function getCalendarStatus(cal) {
  return cal?.status || cal?.calendar_status || 'active';
}

function pickAuthUrl(payload) {
  if (!payload || typeof payload !== 'object') return '';
  return payload.authorization_url || payload.url || '';
}

export default function AgentCalendarPage() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const oauthReturnHandledRef = useRef(false);

  useEffect(() => {
    oauthReturnHandledRef.current = false;
  }, [agentId]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calendar, setCalendar] = useState(null);
  const [missingCalendar, setMissingCalendar] = useState(false);
  const [integrations, setIntegrations] = useState([]);
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [oauthStarting, setOauthStarting] = useState(null);

  const [parallelCapacity, setParallelCapacity] = useState(1);
  const [timezone, setTimezone] = useState('America/New_York');
  const [slotDuration, setSlotDuration] = useState('30');
  const [workingRows, setWorkingRows] = useState(() => normalizeWorkingHours(defaultWorkingHours()));

  const loadCalendar = useCallback(async () => {
    const allowedSlotMinutes = ['15', '30', '45', '60'];
    try {
      setLoading(true);
      const res = await getAgentCalendar({ suppressGlobalErrorToast: true });
      const cal = pickCalendarPayload(res.data);
      setCalendar(cal);
      setMissingCalendar(false);
      setParallelCapacity(Number(cal.parallel_capacity ?? cal.capacity ?? 1) || 1);
      setTimezone(cal.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
      const rawSlot = String(cal.slot_duration_minutes ?? cal.slot_duration ?? 30);
      setSlotDuration(allowedSlotMinutes.includes(rawSlot) ? rawSlot : '30');
      setWorkingRows(normalizeWorkingHours(cal.working_hours));
    } catch (error) {
      if (error.status === 404) {
        setMissingCalendar(true);
        setCalendar(null);
        setWorkingRows(normalizeWorkingHours(defaultWorkingHours()));
      } else {
        toast.error(error.detail || 'Failed to load calendar');
        navigate(`/agents/${agentId}`);
      }
    } finally {
      setLoading(false);
    }
  }, [agentId, navigate]);

  const loadIntegrations = useCallback(async () => {
    if (!agentId) return;
    try {
      setIntegrationsLoading(true);
      const res = await getCalendarIntegrations(agentId, { suppressGlobalErrorToast: true });
      const list = res.data?.integrations ?? res.data;
      setIntegrations(Array.isArray(list) ? list : []);
    } catch {
      setIntegrations([]);
    } finally {
      setIntegrationsLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  useEffect(() => {
    if (!loading && !missingCalendar && calendar) {
      loadIntegrations();
    }
  }, [loading, missingCalendar, calendar, loadIntegrations]);

  useEffect(() => {
    if (!agentId || typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const status = params.get('calendar_oauth');
    if (!status) return;
    if (oauthReturnHandledRef.current) return;
    oauthReturnHandledRef.current = true;

    const provider = params.get('calendar_oauth_provider');
    const msg = params.get('calendar_oauth_message');
    if (status === 'success') {
      toast.success(provider === 'google' ? 'Google Calendar connected' : 'Calendar connected');
    } else {
      toast.error(msg ? decodeURIComponent(msg) : 'Could not connect calendar');
    }

    params.delete('calendar_oauth');
    params.delete('calendar_oauth_provider');
    params.delete('calendar_oauth_message');
    const qs = params.toString();
    navigate(`/agents/${agentId}/calendar${qs ? `?${qs}` : ''}`, { replace: true });

    loadCalendar();
    loadIntegrations();
  }, [agentId, navigate, loadCalendar, loadIntegrations]);

  const publicBookingUrl = useMemo(() => {
    const token = getPublicToken(calendar);
    if (!token) return '';
    const origin = getPublicAppBaseUrl();
    if (!origin) return '';
    return `${origin}/c/${encodeURIComponent(token)}`;
  }, [calendar]);

  const handleCreateCalendar = async () => {
    try {
      setSaving(true);
      const body = {
        parallel_capacity: Number(parallelCapacity) || 1,
        timezone,
        slot_duration_minutes: Number(slotDuration) || 30,
        working_hours: workingRows
          .filter((row) => !row.closed && row.intervals?.length)
          .map((row) => ({
            weekday: row.weekday,
            intervals: row.intervals,
          })),
      };
      const res = await createAgentCalendar(body);
      const cal = pickCalendarPayload(res.data);
      setCalendar(cal);
      setMissingCalendar(false);
      toast.success('Calendar created');
    } catch (error) {
      toast.error(error.detail || 'Could not create calendar');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    if (missingCalendar) {
      await handleCreateCalendar();
      return;
    }
    try {
      setSaving(true);
      const body = {
        parallel_capacity: Number(parallelCapacity) || 1,
        timezone,
        slot_duration_minutes: Number(slotDuration) || 30,
        working_hours: workingRows
          .filter((row) => !row.closed && row.intervals?.length)
          .map((row) => ({
            weekday: row.weekday,
            intervals: row.intervals,
          })),
      };
      const res = await patchAgentCalendar(body);
      const cal = pickCalendarPayload(res.data);
      setCalendar(cal || { ...calendar, ...body });
      toast.success('Calendar settings saved');
    } catch (error) {
      toast.error(error.detail || 'Could not save settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePauseToggle = async () => {
    const status = getCalendarStatus(calendar);
    try {
      setSaving(true);
      if (status === 'paused') {
        await resumeAgentCalendar();
        toast.success('Calendar resumed');
      } else {
        await pauseAgentCalendar();
        toast.success('Calendar paused');
      }
      await loadCalendar();
    } catch (error) {
      toast.error(error.detail || 'Could not update calendar status');
    } finally {
      setSaving(false);
    }
  };

  const handleRotateToken = async () => {
    if (!window.confirm('Rotate the public link? The old booking link will stop working.')) {
      return;
    }
    try {
      setSaving(true);
      const res = await rotateAgentCalendarPublicToken();
      const cal = pickCalendarPayload(res.data);
      setCalendar(cal || calendar);
      toast.success('Public link rotated');
    } catch (error) {
      toast.error(error.detail || 'Could not rotate link');
    } finally {
      setSaving(false);
    }
  };

  const returnUrlForOAuth = useMemo(() => {
    if (!agentId) return '';
    const origin = getPublicAppBaseUrl();
    if (!origin) return '';
    return `${origin}/agents/${agentId}/calendar`;
  }, [agentId]);

  const handleConnectGoogle = async () => {
    if (!agentId) {
      toast.error('Missing agent');
      return;
    }
    try {
      setOauthStarting('google');
      const res = await startCalendarOAuthGoogle(agentId, { return_url: returnUrlForOAuth });
      const url = pickAuthUrl(res.data);
      if (!url) {
        toast.error('Server did not return a Google authorization URL');
        return;
      }
      window.location.href = url;
    } catch (error) {
      const n = getApiError(error);
      toast.error(n.detail || n.uiMessage || error.message || 'Could not start Google Calendar connection');
      setOauthStarting(null);
    }
  };

  const handleDisconnectIntegration = async (provider) => {
    const label = provider === 'google' ? 'Google Calendar' : 'External calendar';
    if (!window.confirm(`Disconnect ${label}? Sync with Desqly will stop.`)) return;
    try {
      setSaving(true);
      await disconnectCalendarIntegration(agentId, provider);
      toast.success('Calendar disconnected');
      await loadIntegrations();
    } catch (error) {
      const n = getApiError(error);
      toast.error(n.detail || n.uiMessage || 'Could not disconnect');
    } finally {
      setSaving(false);
    }
  };

  const copyPublicLink = async () => {
    if (!publicBookingUrl) return;
    try {
      await navigator.clipboard.writeText(publicBookingUrl);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const updateDayClosed = (weekday, closed) => {
    setWorkingRows((rows) =>
      rows.map((row) =>
        row.weekday === weekday
          ? {
              ...row,
              closed,
              intervals: closed ? [] : row.intervals?.length ? row.intervals : [{ start: '09:00', end: '17:00' }],
            }
          : row
      )
    );
  };

  const updateInterval = (weekday, index, field, value) => {
    setWorkingRows((rows) =>
      rows.map((row) => {
        if (row.weekday !== weekday) return row;
        const intervals = [...(row.intervals || [])];
        const next = { ...intervals[index], [field]: value };
        intervals[index] = next;
        return { ...row, intervals };
      })
    );
  };

  if (loading) {
    return (
      <div className="space-y-6 max-w-4xl">
        <Skeleton className="h-8 w-48" />
        <Card>
          <CardContent className="p-6 space-y-4">
            <Skeleton className="h-24" />
            <Skeleton className="h-40" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-4xl" data-testid="agent-calendar-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Button variant="ghost" className="-ml-2 w-fit" asChild>
            <Link to={`/agents/${agentId}`}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to agent
            </Link>
          </Button>
          <h1 className="text-2xl sm:text-3xl font-jakarta font-bold text-slate-900 flex items-center gap-2 mt-2">
            <CalendarIcon className="w-7 h-7 text-teal-600" />
            Booking calendar
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2 justify-end">
          {calendar && !missingCalendar && (
            <Button variant="outline" size="sm" asChild>
              <Link to="/appointments">My appointments</Link>
            </Button>
          )}
          {calendar && (
            <Badge variant={getCalendarStatus(calendar) === 'paused' ? 'destructive' : 'default'}>
              {getCalendarStatus(calendar) === 'paused' ? 'Paused' : 'Accepting bookings'}
            </Badge>
          )}
        </div>
      </div>

      {missingCalendar && (
        <Card className="border-dashed border-teal-200 bg-teal-50/50">
          <CardHeader>
            <CardTitle className="text-lg">Create a calendar</CardTitle>
            <CardDescription>
              No scheduling calendar exists for this agent yet. Defaults are weekdays 9:00–17:00, 30-minute
              slots, and one booking per slot. You can edit everything after creation.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleCreateCalendar} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create calendar
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Availability</CardTitle>
              <CardDescription>Parallel bookings per time slot, slot length, and weekly hours.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="capacity">Parallel capacity</Label>
                  <Input
                    id="capacity"
                    type="number"
                    min={1}
                    value={parallelCapacity}
                    onChange={(e) => setParallelCapacity(Number(e.target.value))}
                  />
                  <p className="text-xs text-slate-500">Max simultaneous appointments per slot.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tz">Timezone (IANA)</Label>
                  <Input
                    id="tz"
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    placeholder="America/New_York"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Slot duration</Label>
                  <Select value={slotDuration} onValueChange={setSlotDuration}>
                    <SelectTrigger>
                      <SelectValue placeholder="Minutes" />
                    </SelectTrigger>
                    <SelectContent>
                      {['15', '30', '45', '60'].map((m) => (
                        <SelectItem key={m} value={m}>
                          {m} minutes
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <Label>Working days</Label>
                <div className="space-y-3">
                  {workingRows.map((row) => (
                    <div
                      key={row.weekday}
                      className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50/80 p-3 sm:flex-row sm:items-center"
                    >
                      <div className="flex items-center justify-between gap-3 sm:w-40">
                        <span className="text-sm font-medium text-slate-800">
                          {WEEKDAY_META.find((d) => d.weekday === row.weekday)?.label}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500">Closed</span>
                          <Switch
                            checked={Boolean(row.closed)}
                            onCheckedChange={(checked) => updateDayClosed(row.weekday, checked)}
                          />
                        </div>
                      </div>
                      {!row.closed && row.intervals?.[0] && (
                        <div className="flex flex-1 flex-wrap items-end gap-2">
                          <div className="space-y-1">
                            <Label className="text-xs text-slate-500">Start</Label>
                            <Input
                              type="time"
                              value={row.intervals[0].start}
                              onChange={(e) => updateInterval(row.weekday, 0, 'start', e.target.value)}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-slate-500">End</Label>
                            <Input
                              type="time"
                              value={row.intervals[0].end}
                              onChange={(e) => updateInterval(row.weekday, 0, 'end', e.target.value)}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button onClick={handleSaveSettings} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {missingCalendar ? 'Save & create' : 'Save settings'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Public booking link</CardTitle>
              <CardDescription>Share this URL with customers. Each agent has a unique link.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {missingCalendar || !getPublicToken(calendar) ? (
                <p className="text-sm text-slate-500">Create the calendar to generate a public link.</p>
              ) : (
                <>
                  <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs break-all">
                    <Link2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span>{publicBookingUrl}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={copyPublicLink}>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy
                    </Button>
                    <Button type="button" variant="outline" size="sm" asChild>
                      <a href={publicBookingUrl} target="_blank" rel="noreferrer">
                        Open
                      </a>
                    </Button>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-slate-600"
                    onClick={handleRotateToken}
                    disabled={saving || missingCalendar}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Rotate link
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">External calendars</CardTitle>
              <CardDescription>
                Connect Google Calendar so bookings and busy times can stay in sync.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {missingCalendar ? (
                <p className="text-sm text-slate-500">Create the booking calendar above before connecting.</p>
              ) : (
                <>
                  <div className="flex flex-col gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="justify-start h-auto py-3 px-3"
                      onClick={handleConnectGoogle}
                      disabled={Boolean(oauthStarting) || saving || integrationsLoading}
                    >
                      <Cloud className="w-5 h-5 mr-3 text-sky-600 shrink-0" />
                      <span className="text-left">
                        <span className="font-medium block">Google Calendar</span>
                        <span className="text-xs text-slate-500 font-normal">Sign in with Google.</span>
                      </span>
                      {oauthStarting === 'google' ? (
                        <Loader2 className="w-4 h-4 ml-auto animate-spin shrink-0" />
                      ) : null}
                    </Button>
                  </div>

                  {integrationsLoading ? (
                    <p className="text-xs text-slate-500">Loading connections…</p>
                  ) : integrations.length > 0 ? (
                    <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/80 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">Connected</p>
                        <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" asChild>
                          <Link to="/appointments">Sync on calendar</Link>
                        </Button>
                      </div>
                      <ul className="space-y-2">
                        {integrations.map((row) => {
                          const provider = row.provider || row.type;
                          const label = provider === 'google' ? 'Google Calendar' : provider || 'Calendar';
                          const email = row.email || row.account_email || '';
                          const err = row.last_error || row.error;
                          return (
                            <li
                              key={provider || label}
                              className="flex flex-col gap-1 rounded-md border border-slate-100 bg-white px-3 py-2 text-sm"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="font-medium text-slate-800">{label}</span>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-slate-600"
                                  onClick={() => handleDisconnectIntegration(provider)}
                                  disabled={saving}
                                >
                                  <Unplug className="w-3.5 h-3.5 mr-1" />
                                  Disconnect
                                </Button>
                              </div>
                              {email ? <p className="text-xs text-slate-500">{email}</p> : null}
                              {row.calendar_summary ? (
                                <p className="text-xs text-slate-500">{row.calendar_summary}</p>
                              ) : null}
                              {row.last_sync_at ? (
                                <p className="text-xs text-slate-400">
                                  Last sync: {new Date(row.last_sync_at).toLocaleString()}
                                </p>
                              ) : null}
                              {row.status && row.status !== 'active' ? (
                                <Badge variant="secondary" className="w-fit text-[10px]">
                                  {row.status}
                                </Badge>
                              ) : null}
                              {err ? <p className="text-xs text-amber-800">{String(err)}</p> : null}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500">No external calendar linked yet. Connect Google Calendar above.</p>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Pause & resume</CardTitle>
              <CardDescription>While paused, customers cannot book new appointments from the public page.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                type="button"
                variant={getCalendarStatus(calendar) === 'paused' ? 'default' : 'outline'}
                className={
                  getCalendarStatus(calendar) === 'paused'
                    ? 'bg-teal-600 hover:bg-teal-700'
                    : 'border-amber-300 text-amber-900'
                }
                onClick={handlePauseToggle}
                disabled={saving || missingCalendar || !calendar}
              >
                {getCalendarStatus(calendar) === 'paused' ? (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Resume calendar
                  </>
                ) : (
                  <>
                    <Pause className="w-4 h-4 mr-2" />
                    Pause calendar
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
